import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, AlertTitle, Box, CircularProgress, Stack, Typography } from "@mui/material";
import { api, getToken, v } from "../../utils/helpers";

const LIMIT = 4000;
const identity = () => { try { return getToken(); } catch { return null; } };
const cleanText = value => typeof value === "string" ? value.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, "").trim().slice(0, LIMIT) : "";

export function sapRuntimeMessage(value) {
  const raw = cleanText(value);
  if (!raw) return "";
  try {
    const parsed = JSON.parse(raw), message = parsed?.message;
    const main = typeof message === "string" ? message : cleanText(message?.messageText);
    const parameter = parsed?.parameter ?? message?.parameter;
    const parameters = typeof parameter === "string" ? [parameter]
      : Array.isArray(parameter) ? parameter.filter(item => typeof item === "string") : [];
    const parts = [...new Set([main, ...parameters].map(cleanText).filter(Boolean))];
    return parts.length ? parts.join("\n").slice(0, LIMIT) : raw;
  } catch { return raw; }
}

// Mounted only by the opened package dialog for the exact current run. No polling.
function CurrentRuntimeErrors({ instanceId, flowIds, scope }) {
  const { t } = useTranslation("freigabe");
  const token = identity();
  const key = JSON.stringify([scope, instanceId, flowIds]);
  const [state, setState] = useState({ key: null, token: null, rows: {} });
  useEffect(() => {
    const [, targetId, ids] = JSON.parse(key);
    let active = true, cursor = 0, denied = false;
    const controller = new AbortController(), rows = {};
    setState({ key, token, rows: {} });
    async function worker() {
      while (active && !denied && identity() === token && cursor < ids.length) {
        const flowId = ids[cursor++];
        try {
          const result = await api(`/api/${encodeURIComponent(targetId)}/runtime-error/${encodeURIComponent(flowId)}`,
            { signal: controller.signal, retry: false, timeout: 35000 });
          if (!result || result.flowId !== flowId) throw new Error("RUNTIME_ERROR_IDENTITY_MISMATCH");
          if (active && !denied && identity() === token) rows[flowId] = { loaded: true, text: sapRuntimeMessage(result.errorText) };
        } catch (error) {
          if ([401, 403].includes(error?.status)) { denied = true; controller.abort(); }
          if (active && identity() === token) rows[flowId] = { failed: true };
        }
        if (active && identity() === token) setState({ key, token, rows: denied
          ? Object.fromEntries(ids.map(id => [id, { failed: true }])) : { ...rows } });
      }
    }
    Promise.all(Array.from({ length: Math.min(2, ids.length) }, worker)).then(() => {
      if (active && denied && identity() === token) setState({ key, token, rows: Object.fromEntries(ids.map(id => [id, { failed: true }])) });
    });
    return () => { active = false; controller.abort(); };
  }, [key, token]);
  const current = state.key === key && state.token === token ? state.rows : {};
  return <Stack spacing={1.5} sx={{ mt: 1.5 }}>
    <Typography component="h4" variant="body2" sx={{ fontWeight: 600 }}>{t("stageBranches.details.deployment.currentSap")}</Typography>
    {flowIds.map(id => <Box key={id} sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ overflowWrap: "anywhere", fontWeight: 600 }}>{id}</Typography>
      {!current[id] ? <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={14} aria-label={t("stageBranches.details.deployment.loading")}/><Typography variant="body2">{t("stageBranches.details.deployment.loading")}</Typography></Stack>
        : <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", color: v("text-1") }}>
          {current[id].failed ? t("stageBranches.details.deployment.unavailable") : current[id].text || t("stageBranches.details.deployment.noCurrentError")}
        </Typography>}
    </Box>)}
  </Stack>;
}

export default function DeploymentFailures({ failures, run, scope, runtimeArtifactIds = [], enabled = false }) {
  const { t } = useTranslation("freigabe");
  const rows = (Array.isArray(failures) ? failures : []).filter(row => row && typeof row.artifactId === "string" && row.phase === "deployment");
  if (!enabled || !rows.length) return null;
  const flowIds = [...new Set(rows.filter(row => !row.type || row.type === "integration-flow").map(row => row.artifactId))]
    .filter(id => id.length > 0 && id.length <= 200 && runtimeArtifactIds.includes(id)).sort();
  const instanceId = run?.uploadConfirmed === true ? run.targetInstanceId : null;
  return <Alert severity="error" variant="outlined" sx={{ color: v("text-1"), background: "transparent", overflowWrap: "anywhere" }}>
    <AlertTitle>{t("stageBranches.details.deployment.title")}</AlertTitle>
    <Typography variant="body2">{t("stageBranches.details.deployment.uploaded")}</Typography>
    <Stack component="ul" spacing={1} sx={{ my: 1.5, pl: 2.5 }}>
      {rows.map((row, index) => <Box component="li" key={`${row.artifactId}:${index}`}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.artifactId}</Typography>
        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{sapRuntimeMessage(row.message) || t("stageBranches.details.deployment.failed")}</Typography>
      </Box>)}
    </Stack>
    {instanceId && run?.runId && flowIds.length > 0 && <CurrentRuntimeErrors instanceId={instanceId} flowIds={flowIds}
      scope={JSON.stringify([scope, run.runId, run.runAttempt || 1])}/>}
  </Alert>;
}
